import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ApiResponse, PageInfo } from '../../../models';
import { HttpService } from '../http.service';

export type SectionBannerType = 'Category' | 'Brand' | 'Product' | 'Tag' | 'Deal';
export type SectionLanguage = 'en-us' | 'ar-ae';
export interface SectionTranslation {
  language: SectionLanguage;
  view_type: { desktop: { image_url: string }; mobile: { image_url: string } };
}
export interface SectionBannerInput {
  banner_type: SectionBannerType;
  banner_value: number;
  translations: SectionTranslation[];
}
export interface SectionBannerRecord {
  id: number | string;
  position: number;
  banner_type: SectionBannerType;
  banner_value: number | string;
  translation?: SectionTranslation;
  translations?: SectionTranslation[];
}
export interface SectionBannerPage {
  records: SectionBannerRecord[];
  pagination: PageInfo;
}
export interface SectionFeature {
  is_enabled: boolean;
}

interface SectionBannerConfig {
  routePrefix: 'bazaar' | 'refurbish';
  featureEntity: 'bazaar' | 'refurbished';
  orderKey: 'bazaar_banners' | 'refurbished_banners';
  label: string;
}

/** Shared feature/carousel contract only. Section-specific settings stay in their own client. */
export abstract class SectionBannersService extends HttpService<unknown, unknown> {
  constructor(http: HttpClient, url: string, version: string, private readonly section: SectionBannerConfig) {
    super(http, url, version, section.routePrefix);
  }

  getFeature(): Observable<SectionFeature> {
    return this.request('GET', `feature/${this.section.featureEntity}`);
  }

  saveFeature(is_enabled: boolean): Observable<SectionFeature> {
    return this.request('PATCH', `feature/${this.section.featureEntity}/toggle`, { is_enabled });
  }

  getBanners(page = 1, limit = 10): Observable<SectionBannerPage> {
    return this.request('GET', `${this.section.routePrefix}/banners/admin/list?page=${page}&limit=${limit}`);
  }

  getBanner(id: number): Observable<SectionBannerRecord> {
    return this.request('GET', `${this.section.routePrefix}/banners/${id}`);
  }

  saveBanner(input: SectionBannerInput, id: number | null): Observable<SectionBannerRecord> {
    return this.request(id === null ? 'POST' : 'PUT', `${this.section.routePrefix}/banners${id === null ? '' : `/${id}`}`, input);
  }

  deleteBanner(id: number): Observable<unknown> {
    return this.request('DELETE', `${this.section.routePrefix}/banners/${id}`);
  }

  reorderBanners(banners: { id: number; position: number }[]): Observable<unknown> {
    return this.request('PUT', `${this.section.routePrefix}/banners/positions/align`, { [this.section.orderKey]: banners });
  }

  protected request<T>(method: string, path: string, body?: unknown): Observable<T> {
    return this.httpClient.request<ApiResponse<T>>(method, `${this.apiUrlNoEnd}/${path}`, { body }).pipe(
      map((response) => {
        if (response.status !== 'success' || response.data == null) {
          throw new Error(response.message || `The ${this.section.label} API returned an invalid response.`);
        }
        return response.data;
      })
    );
  }
}
